import { useQuery, useMutation, useQueryClient } from 'react-query'
import { config } from 'config'
import { callAxios, CustomAxiosError } from 'utils/api'
import { DEPOT_INBOUND_TARGET_DEFAULT_VALUE } from 'constants/supplierTarget'
import {
  DepotInboundTargetData,
  GetDepotInboundTargetRequest,
  GetDepotInboundTargetRawResponse,
  SetDepotInboundTargetRequest,
  SetDepotInboundTargetResponse,
} from 'types/supplierTarget'

const { SECURE_BASE_URL } = config!

const getDepotInboundTarget = async (props: GetDepotInboundTargetRequest): Promise<DepotInboundTargetData> => {
  try {
    const { response: targetResponse } = await callAxios<{
      response: GetDepotInboundTargetRawResponse
    }>({
      url: `${SECURE_BASE_URL}/getinboundtarget`,
      method: 'GET',
    })

    const mappedResponse = (): DepotInboundTargetData => {
      const targetNumberString = targetResponse?.data
        ?.find((item) => !!item?.inboundTarget)
        ?.inboundTarget.replace(/\D/g, '')

      const targetNumberValue =
        !!targetNumberString && !isNaN(parseInt(targetNumberString)) ? parseInt(targetNumberString) : null

      return {
        depotInboundTarget: targetNumberValue ?? DEPOT_INBOUND_TARGET_DEFAULT_VALUE,
      }
    }

    return mappedResponse()
  } catch (e) {
    //Fallback in case API fails, still load default target value
    return { depotInboundTarget: DEPOT_INBOUND_TARGET_DEFAULT_VALUE }
  }
}

export const useGetDepotInboundTarget = (props: GetDepotInboundTargetRequest) => {
  const {
    status: getDepotInboundTargetStatus,
    isFetching: getIsDepotInboundTargetLoading,
    data: depotInboundTargetData,
    error: getDepotInboundTargetError,
  } = useQuery<DepotInboundTargetData, CustomAxiosError>(['depotInboundTarget/get', props], () =>
    getDepotInboundTarget(props)
  )

  return {
    getDepotInboundTargetStatus,
    getIsDepotInboundTargetLoading,
    depotInboundTargetData,
    getDepotInboundTargetError,
  }
}

const setDepotInboundTarget = async (props: SetDepotInboundTargetRequest): Promise<SetDepotInboundTargetResponse> => {
  const { response: targetResponse } = await callAxios<{
    response: SetDepotInboundTargetResponse
  }>({
    url: `${SECURE_BASE_URL}/setinboundtarget`,
    method: 'POST',
    data: {
      inboundTarget: `${props.depotInboundTarget?.toString() ?? DEPOT_INBOUND_TARGET_DEFAULT_VALUE.toString()}%`,
      updatedBy: props.updatedBy,
    },
  })

  return targetResponse
}

export const useSetDepotInboundTarget = () => {
  const queryClient = useQueryClient()

  const {
    status: getSetDepotInboundTargetStatus,
    data: setDepotInboundTargetData,
    error: getSetDepotInboundTargetError,
    mutate: mutateSetDepotInboundTarget,
    reset: resetSetDepotInboundTarget,
  } = useMutation<SetDepotInboundTargetResponse, CustomAxiosError, SetDepotInboundTargetRequest>(
    setDepotInboundTarget,
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(['depotInboundTarget/get'])
      },
    }
  )

  return {
    getSetDepotInboundTargetStatus,
    setDepotInboundTargetData,
    getSetDepotInboundTargetError,
    mutateSetDepotInboundTarget,
    resetSetDepotInboundTarget,
  }
}
